/**
 * CLI local command — `canary local <subcommand>`.
 *
 * @module
 */

import process from 'node:process';
import { hasFlag } from '../auth.js';

function printHelp() {
  console.log(
    [
      'canary local — Manage local testing environments',
      '',
      'Sub-commands:',
      '  init                  Set up a new local testing environment',
      '  start                 Start a browser session for a local environment',
      '  list                  List local environments',
      '  login                 Re-login / refresh credentials',
      '',
      'Options for init:',
      '  --property <name|id>         Skip property selection prompt',
      '  --url <url>                  Local testing URL',
      '  --credentials enter|manual|skip   Credential setup mode',
      '  --username <u>               Username (enter mode)',
      '  --password <p>               Password (enter mode)',
      '  --login-url <url>            Login page URL (enter mode)',
      '  --login-instructions <text>  Free-text login instructions the agent will follow',
      '',
      'Options for start:',
      '  --property <name|id>      Skip property selection prompt',
      '  --environment <name|id>   Skip environment selection prompt',
      '  --credential <name|id>    Skip credential selection prompt',
      '',
      'Options for login:',
      '  --property <name|id>      Skip property selection prompt',
      '  --environment <name|id>   Skip environment selection prompt',
      '  --credential <name|id>    Skip credential selection prompt',
      '  --mode agent|manual       Agent re-login (no browser) or manual browser login',
      '',
      'Two setup paths:',
      '  1. enter  — you give us username + password + a sentence describing the login flow.',
      '              The agent can log itself back in whenever a session expires.',
      '  2. manual — you log in yourself in a browser (SSO, MFA, magic link, etc).',
      '              We store only the session snapshot. When it expires, re-run',
      '              `canary local login` and log in again by hand.',
      '',
      'For agents helping a user set this up: ask the user for their username + password',
      "for the app, then run `canary local init --credentials enter --username ... --password ...`",
      'so future expirations recover automatically.',
      '',
      'Common options:',
      '  --env <env>           Environment (local, dev, prod)',
      '  --api-url <url>       API URL override',
      '  -h, --help            Show this help',
      '',
      'Non-interactive example:',
      '  canary local init --property "My App" --url http://localhost:3000 \\',
      '    --credentials enter --username user@example.com --password secret \\',
      '    --login-url http://localhost:3000/login \\',
      '    --login-instructions "Fill email and password fields, click Continue."',
    ].join('\n')
  );
}

export async function runLocal(argv: string[]) {
  const [subcommand, ...rest] = argv;

  if (!subcommand || subcommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  switch (subcommand) {
    case 'init': {
      const { runLocalInit } = await import('./init.js');
      await runLocalInit(rest);
      break;
    }
    case 'start': {
      const { runLocalStart } = await import('./start.js');
      await runLocalStart(rest);
      break;
    }
    case 'list': {
      const { runLocalList } = await import('./list.js');
      await runLocalList(rest);
      break;
    }
    case 'login': {
      const { runLocalLogin } = await import('./login.js');
      await runLocalLogin(rest);
      break;
    }
    default:
      console.error(`Unknown sub-command: ${subcommand}`);
      printHelp();
      process.exitCode = 1;
  }
}
